import * as Sentry from "@sentry/react";

/**
 * Sentry observability for BADDY (React + Vite).
 *
 * Environment variables (Vite, client-side — these are public by design):
 *   VITE_SENTRY_DSN          required to enable Sentry. If absent, Sentry is a no-op.
 *   VITE_SENTRY_ENVIRONMENT  optional override ("production" | "staging" | "development")
 *   VITE_APP_VERSION         optional release identifier (e.g. git sha) used for source maps
 *
 * Build-time only (never exposed to the browser, set in CI):
 *   SENTRY_AUTH_TOKEN, SENTRY_ORG, SENTRY_PROJECT  → source map upload
 */

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

export const SENTRY_ENVIRONMENT =
  (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
  (import.meta.env.PROD ? "production" : "development");

const IS_PROD = SENTRY_ENVIRONMENT === "production";

export const sentryEnabled = Boolean(DSN);

/** Keys whose values must never leave the browser. */
const SENSITIVE_KEY = /(token|secret|password|passwd|authorization|apikey|api_key|card|cvc|cvv|iban|account_number|routing|ssn|client_secret)/i;

function scrub<T>(value: T, depth = 0): T {
  if (depth > 6 || value == null) return value;
  if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1)) as unknown as T;
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = SENSITIVE_KEY.test(k) ? "[Filtered]" : scrub(v, depth + 1);
    }
    return out as unknown as T;
  }
  return value;
}

/** Strip query strings/fragments that might carry tokens. */
function scrubUrl(url?: string): string | undefined {
  if (!url) return url;
  try {
    const u = new URL(url, window.location.origin);
    for (const key of [...u.searchParams.keys()]) {
      if (SENSITIVE_KEY.test(key) || key === "code" || key === "access_token") {
        u.searchParams.set(key, "[Filtered]");
      }
    }
    u.hash = "";
    return u.toString();
  } catch {
    return url;
  }
}

export function initSentry() {
  if (!DSN) {
    if (import.meta.env.DEV) {
      console.info("[sentry] VITE_SENTRY_DSN not set — error reporting disabled");
    }
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: SENTRY_ENVIRONMENT,
    release: (import.meta.env.VITE_APP_VERSION as string | undefined) || undefined,

    // Performance monitoring (basic): full sampling in dev, 10% in production.
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: IS_PROD ? 0.1 : 1.0,
    tracePropagationTargets: [/^\//, /supabase\.co/],

    // Session replay only on errors, heavily masked.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: IS_PROD ? 0.1 : 0,

    // Privacy: never attach IP / cookies / raw request bodies.
    sendDefaultPii: false,

    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
      // NOTE: dynamic-import / chunk-load failures are intentionally NOT
      // ignored — they are reported with their own fingerprint from
      // src/lib/chunk-errors.ts so we get deploy-health alerts.
    ],


    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === "console" && breadcrumb.level === "debug") return null;
      if (breadcrumb.data?.url) {
        breadcrumb.data.url = scrubUrl(String(breadcrumb.data.url));
      }
      return breadcrumb;
    },

    beforeSend(event) {
      // Do not report anything from local dev unless explicitly enabled.
      if (!IS_PROD && !import.meta.env.VITE_SENTRY_FORCE) return null;

      if (event.request) {
        event.request.url = scrubUrl(event.request.url);
        delete event.request.cookies;
        delete event.request.headers;
        event.request.data = scrub(event.request.data);
      }
      event.extra = scrub(event.extra);
      event.contexts = scrub(event.contexts);
      return event;
    },
  });
}

/** Attach the authenticated user to every subsequent event. */
export function setSentryUser(
  user: { id: string; email?: string | null } | null,
  role?: string | null,
) {
  if (!sentryEnabled) return;
  if (!user) {
    Sentry.setUser(null);
    Sentry.setTag("user_role", undefined as unknown as string);
    return;
  }
  Sentry.setUser({ id: user.id, email: user.email ?? undefined });
  if (role) Sentry.setTag("user_role", role);
}

type CriticalAction =
  | "checkout"
  | "subscription"
  | "tip"
  | "ppv"
  | "refund"
  | "payout"
  | "connect_onboarding";

/** Breadcrumb helper for money-critical flows. Never pass raw card/token data. */
export function trackCritical(
  action: CriticalAction,
  message: string,
  data?: Record<string, unknown>,
) {
  if (!sentryEnabled) return;
  Sentry.addBreadcrumb({
    category: `baddy.${action}`,
    message,
    level: "info",
    data: scrub(data ?? {}),
  });
}

/** Manually report a handled error with context. */
export function captureError(error: unknown, context?: Record<string, unknown>) {
  if (!sentryEnabled) {
    console.error(error, context);
    return;
  }
  Sentry.captureException(error, { extra: scrub(context ?? {}) });
}

export { Sentry };
